/* eslint-disable @typescript-eslint/no-namespace */
import { Compiler } from '@mode-switch/adapt';
import { DataPath, ExprForm } from '@mode-switch/express';
import { Attr, Model } from '@mode-switch/model';
import { EMPTY_ENCODING, EncField, Encoding, EncodingResult, Guide } from '@mode-vegas/vegas';
import { ModelFieldTypes, SortType, VariableType } from '@mode-vegas/vegas';

import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledAttr, Switch } from '@mode/shared/util-switch';

const guide = new Guide();

export namespace TableEncodingHelpers {
  export function initEmpty(): EncodingResult {
    const encoder = guide.newBatchOperation(EMPTY_ENCODING);
    return encoder.complete();
  }

  export function addFieldToTableEncoding(encoding: Encoding, field: CompiledAttr, at?: string) {
    const encoder = guide.newBatchOperation(encoding);

    switch (field.varType) {
      case Attr.Type.Discrete:
      case Attr.Type.Continuous:
        encoder.addEncFieldToSeries(
          {
            formula: `[${field.attrName}]`,
            variable: { type: VariableType.DISCRETE },
          },
          'text',
          '*',
          at
        );
        break;
    }

    return encoder.complete();
  }

  export function initFilledEncoding(model: FlamingoTypes.ModeModel, viewName: string) {
    //Get all fields in dataset and add to encoding
    const encoder = guide.newBatchOperation(EMPTY_ENCODING);
    const view = Model.Views.find(model, viewName);

    if (view) {
      for (const field of view.attrs) {
        // Don't add broken attrs
        const compiled = CompiledAttr.fromAttr(view, field);
        if (compiled.isValid) {
          encoder.addEncFieldToSeries(
            {
              formula: `[${field.name}]`,
              variable: { type: VariableType.DISCRETE },
            },
            'text',
            '*'
          );
        }
      }
    }

    return encoder.complete();
  }

  /** Add sort to encoding */
  export function addSort(
    encoding: Encoding,
    formula: string,
    descending: boolean,
    clearExistingSorts = true
  ): EncodingResult {
    const encoder = guide.newBatchOperation(encoding);

    // Clear all existing sorts before adding a new sort as we only currently support one sort at a time
    if (clearExistingSorts) {
      encoder.clearSorts();
    }
    encoder.addSort({
      type: SortType.DATA,
      source: formula,
      descending,
    });

    return encoder.complete();
  }

  /** Remove sort from encoding */
  export function removeSort(encoding: Encoding, formula: string): EncodingResult {
    return guide.removeSort(encoding, formula);
  }

  /** Clear all existing sorts from encoding */
  export function clearSorts(encoding: Encoding): EncodingResult {
    return guide.clearSorts(encoding);
  }

  /** Clear all existing filters from encoding */
  export function clearFilters(encoding: Encoding): EncodingResult {
    return guide.clearFilters(encoding);
  }

  export function buildAddFieldParams(field: ModelFieldTypes): EncField {
    return {
      formula: `[${field.name}]`,
      variable: { type: VariableType.DISCRETE },
    };
  }

  export function removeField(enc: Encoding, id: string): EncodingResult {
    return guide.removeField(enc, id);
  }

  export function clearColumns(enc: Encoding): EncodingResult {
    const encoder = guide.newBatchOperation(enc);
    enc.marks.properties.text.values.forEach((v) => {
      if (v.id) {
        encoder.removeField(v.id);
      }
    });

    return encoder.complete();
  }

  export function addRemainingColumns(
    enc: Encoding,
    model: FlamingoTypes.ModeModel,
    viewName: string
  ): EncodingResult | null {
    let modified = false;
    const encoder = guide.newBatchOperation(enc);
    const text = enc.marks.properties.text.values;
    // @TODO: (@anthonysimone) - Followup at some point to see if we can do this without both view and modeView.
    const view = Model.Views.find(model, viewName);
    const modeView = Switch.Views.find(model, viewName);

    if (view && modeView) {
      for (const field of view.attrs) {
        const formula = `[${field.name}]`;
        if (text.every((t) => t.formula !== formula)) {
          const compiledAttr = CompiledAttr.fromAttr(modeView, field);
          if (!compiledAttr.isNonScalarCalc && compiledAttr.isValid) {
            // Don't allow aggregate calcs into the table
            encoder.addEncFieldToSeries(
              {
                formula: `[${field.name}]`,
                variable: { type: VariableType.DISCRETE },
              },
              'text',
              '*'
            );
            modified = true;
          }
        }
      }
    }

    if (modified) {
      return encoder.complete();
    }

    return null; // So we don't have to needlessly dispatch an encoding update
  }

  export function hasField(enc: Encoding, formula: string): boolean {
    return enc.marks.properties.text.values.some((f) => f.formula === formula);
  }

  export function hasFields(enc: Encoding) {
    return enc.marks.properties.text.values.length > 0;
  }

  export function getField(enc: Encoding, formula: string): EncField | undefined {
    return enc.marks.properties.text.values.find((field) => field.formula === formula);
  }

  /**
   * Given a formula, get the name of the source field.
   *
   * This is useful to find the table column that a date filter formula is related to, as the date filter will
   * not have the same formula as the column itself.
   */
  export function getColNameFromFilterFormula(
    model: FlamingoTypes.ModeModel,
    queryToken: string,
    formula: string
  ): string {
    const result = Switch.compile(model, queryToken, formula);
    let resultingColumnName = '';

    if (result.type === Compiler.Result.Type.Success) {
      const { expr } = result;

      switch (expr.form) {
        case ExprForm.Access:
        case ExprForm.Aggregate:
        case ExprForm.DateTrunc:
        case ExprForm.DatePart:
          resultingColumnName = DataPath.name(expr.path);
          break;
        default:
          // Unknown here, no name available
          break;
      }
    }

    return resultingColumnName;
  }
}
